import React, { useState } from 'react';
import { apiRequest } from '../config/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

interface DadosLogisticosProps {
  filialAtual?: string;
}

interface Produto {
  id: string;
  codigo: string;
  descricao: string;
  fornecedor: string;
  ean: string;
  embalagemUnd: string;
  alturaUnd: number;
  larguraUnd: number;
  comprimentoUnd: number;
  pesoBrutoUnd: number;
  pesoLiqUnd: number;
  dun: string;
  embCx: string;
  alturaCx: number;
  larguraCx: number;
  comprimentoCx: number;
  pesoBrutoCx: number;
  pesoLiqCx: number;
  ncm: string;
  codFornec: number;
  departamento: string;
  selected: boolean;
}

const DadosLogisticos: React.FC<DadosLogisticosProps> = ({ filialAtual }) => {
  const [tipoBusca, setTipoBusca] = useState<'codigo' | 'descricao' | 'fornecedor'>('codigo');
  const [termoBusca, setTermoBusca] = useState('');
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(false);

  const buscarProdutos = async () => {
    if (!termoBusca.trim()) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('tipo', tipoBusca);
      params.append('termo', termoBusca);
      
      // Mapear filial para código numérico
      const filialMap: Record<string, number> = {
        'FILIAL_1': 1,
        'FILIAL_2': 2,
        'FILIAL_3': 3
      };
      
      if (filialAtual && filialMap[filialAtual]) {
        params.append('codfilial', filialMap[filialAtual].toString());
      }

      const result = await apiRequest(`/winthor/dados-logisticos?${params}`);
      
      const produtosMapeados: Produto[] = (result.dados || []).map((item: any, index: number) => ({
        id: `${item.IDPRODUTO}-${index}`,
        codigo: item.IDPRODUTO,
        descricao: item.DESCRICAO,
        fornecedor: item.FORNECEDOR,
        ean: item.EAN || '',
        embalagemUnd: item.EMBALAGEM_UND || '',
        alturaUnd: item.ALTURA_UND || 0,
        larguraUnd: item.LARGURA_UND || 0,
        comprimentoUnd: item.COMPRIMENTO_UND || 0,
        pesoBrutoUnd: item.PESO_BRUTO_UND || 0,
        pesoLiqUnd: item.PESO_LIQ_UND || 0,
        dun: item.DUN || '',
        embCx: item.EMB_CX || '',
        alturaCx: item.ALTURA_CX || 0,
        larguraCx: item.LARGURA_CX || 0,
        comprimentoCx: item.COMPRIMENTO_CX || 0,
        pesoBrutoCx: item.PESO_BRUTO_CX || 0,
        pesoLiqCx: item.PESO_LIQ_CX || 0,
        ncm: item.NCM || '',
        codFornec: item.CODFORNEC || 0,
        departamento: item.DEPARTAMENTO || '',
        selected: false
      }));

      setProdutos(produtosMapeados);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      alert('Erro ao buscar produtos. Verifique a conexão com WINTHOR.');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelecao = (id: string) => {
    setProdutos(produtos.map(p => 
      p.id === id ? { ...p, selected: !p.selected } : p
    ));
  };

  const selecionarTodos = () => {
    setProdutos(produtos.map(p => ({ ...p, selected: true })));
  };

  const limparSelecao = () => {
    setProdutos(produtos.map(p => ({ ...p, selected: false })));
  };

  const exportarDados = () => {
    const selecionados = produtos.filter(p => p.selected);
    if (selecionados.length === 0) {
      alert('Selecione pelo menos um produto para exportar');
      return;
    }

    const doc = new jsPDF();
    
    // Cores da paleta Falcon Hub
    const colors = {
      darkBlue: '#0F172A',
      blue: '#3B82F6',
      green: '#22C55E',
      gray: '#64748B'
    };

    // Header com gradiente
    doc.setFillColor(15, 23, 42); // darkBlue
    doc.rect(0, 0, 210, 40, 'F');
    
    // Título
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Relatório de Dados Logísticos', 105, 20, { align: 'center' });
    
    // Subtítulo com filial
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const filialNome = filialAtual === 'FILIAL_1' ? 'UP Distribuidora de Produtos e Descartáveis LTDA' :
                      filialAtual === 'FILIAL_2' ? 'Aleplast Comércio de Produtos e Descartáveis LTDA' :
                      filialAtual === 'FILIAL_3' ? 'Alepack Industria e Comercio Plasticos LTDA' : 'Todas as Filiais';
    doc.text(filialNome, 105, 30, { align: 'center' });

    // Data de emissão
    const dataEmissao = new Date().toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(8);
    doc.text(`Emitido em: ${dataEmissao}`, 105, 50, { align: 'center' });

    // Tabela de dados
    const tableData = selecionados.map(p => [
      p.codigo,
      p.descricao,
      p.fornecedor,
      p.ean,
      p.embalagemUnd,
      `${p.alturaUnd}×${p.larguraUnd}×${p.comprimentoUnd}`,
      p.pesoBrutoUnd.toFixed(2),
      p.embCx,
      `${p.alturaCx}×${p.larguraCx}×${p.comprimentoCx}`,
      p.pesoBrutoCx.toFixed(2),
      p.ncm
    ]);

    (doc as any).autoTable({
      startY: 60,
      head: [
        ['Código', 'Descrição', 'Fornecedor', 'EAN', 'Emb. UND', 'Dim. UND', 'Peso UND', 'Emb. CX', 'Dim. CX', 'Peso CX', 'NCM']
      ],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [59, 130, 246], // blue
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7
      },
      bodyStyles: {
        fontSize: 6,
        cellPadding: 2
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      columnStyles: {
        0: { cellWidth: 15 }, // Código
        1: { cellWidth: 35 }, // Descrição
        2: { cellWidth: 30 }, // Fornecedor
        3: { cellWidth: 18 }, // EAN
        4: { cellWidth: 12 }, // Emb. UND
        5: { cellWidth: 18 }, // Dim. UND
        6: { cellWidth: 12 }, // Peso UND
        7: { cellWidth: 12 }, // Emb. CX
        8: { cellWidth: 18 }, // Dim. CX
        9: { cellWidth: 12 }, // Peso CX
        10: { cellWidth: 15 } // NCM
      }
    });

    // Footer com marca Falcon Hub
    const finalY = (doc as any).lastAutoTable.finalY + 20;
    
    // Linha decorativa verde
    doc.setFillColor(34, 197, 94); // green
    doc.rect(0, finalY, 210, 2, 'F');
    
    // Texto do footer
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('FALCON HUB', 105, finalY + 10, { align: 'center' });
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text('Sistema de Gestão de Agendamentos', 105, finalY + 16, { align: 'center' });

    // Salvar PDF
    doc.save(`dados-logisticos-${Date.now()}.pdf`);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Buscar Produtos</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Busca</label>
            <select
              value={tipoBusca}
              onChange={(e) => setTipoBusca(e.target.value as any)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="codigo">Código</option>
              <option value="descricao">Descrição</option>
              <option value="fornecedor">Fornecedor</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {tipoBusca === 'codigo' ? 'Código do Produto' : 
               tipoBusca === 'descricao' ? 'Descrição' : 'Fornecedor'}
            </label>
            <input
              type="text"
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
              placeholder={`Digite o ${tipoBusca}...`}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={buscarProdutos}
              disabled={loading}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
        </div>
      </div>

      {produtos.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              Resultados ({produtos.length})
            </h2>
            <div className="flex gap-2">
              <button
                onClick={selecionarTodos}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Selecionar Todos
              </button>
              <button
                onClick={limparSelecao}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Limpar Seleção
              </button>
              <button
                onClick={exportarDados}
                className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                Exportar Selecionados
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={produtos.every(p => p.selected)}
                      onChange={(e) => e.target.checked ? selecionarTodos() : limparSelecao()}
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fornecedor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">EAN</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Emb. UND</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dim. UND (A×L×C)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Peso UND (kg)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Emb. CX</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dim. CX (A×L×C)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Peso CX (kg)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">NCM</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {produtos.map((produto) => (
                  <tr key={produto.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={produto.selected}
                        onChange={() => toggleSelecao(produto.id)}
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{produto.codigo}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">{produto.descricao}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{produto.fornecedor}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{produto.ean}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{produto.embalagemUnd}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {produto.alturaUnd}×{produto.larguraUnd}×{produto.comprimentoUnd}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{produto.pesoBrutoUnd}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{produto.embCx}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {produto.alturaCx}×{produto.larguraCx}×{produto.comprimentoCx}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{produto.pesoBrutoCx}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{produto.ncm}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {produtos.length === 0 && !loading && termoBusca && (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          Nenhum produto encontrado
        </div>
      )}
    </div>
  );
};

export default DadosLogisticos;
